// Copyright 2022, Imprivata, Inc.  All rights reserved.

import { useQuery } from '@tanstack/react-query';

const getPhotoQueryKey = 'get-photo';

const getPhotoQuery = async (photoUrl: string | undefined): Promise<string> => {
  if (!photoUrl) {
    throw new Error('Photo URL is not provided');
  }

  const photoResponse = await fetch(photoUrl, {
    method: 'GET',
    credentials: 'include', // Include cookies for authentication
    headers: {
      Accept: 'image/jpeg',
      'Cache-Control': 'no-cache',
    },
  });

  if (!photoResponse.ok) {
    if (photoResponse.status === 404) {
      throw new Error('Photo not found');
    }
    if (photoResponse.status === 401) {
      throw new Error('Unauthorized');
    }
    throw new Error(`Failed to fetch photo: ${photoResponse.status}`);
  }

  // Convert the response to a blob and then to a data URL
  const blob = await photoResponse.blob();

  if (blob.size === 0) {
    throw new Error('Empty image response');
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;

      if (!result) {
        reject(new Error('Failed to create data URL'));
        return;
      }

      resolve(result);
    };
    reader.onerror = () => {
      reject(new Error('Failed to read image data'));
    };
    reader.readAsDataURL(blob);
  });
};

export const useGetPhotoQuery = (photoUrl: string | undefined) => {
  const query = useQuery({
    queryKey: [getPhotoQueryKey, photoUrl],
    queryFn: () => (photoUrl ? getPhotoQuery(photoUrl) : Promise.resolve(null)),
    staleTime: Number.POSITIVE_INFINITY,
    enabled: !!photoUrl,
    retry: (failureCount, error) => {
      // Don't retry on "not-found" errors
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('not found') || errorMessage.includes('Unauthorized')) {
        return false;
      }
      // Retry up to 3 times for server errors and network errors
      return failureCount < 3;
    },
  });

  return {
    photo: query.data,
    isLoading: query.isLoading,
  };
};
